import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { CdkConnectedOverlay, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';

import { MembersService } from '../../../../services/members/members.service';
import { Membership } from '../../../../models/entities/membership';
import { TagsService } from '../../../../services/tags/tags.service';
import { TagBatchItem } from '../../../../models/entities/tag-batch-item';
import {
    mapMembershipToPicker,
    PickerMemberOptions,
    PickerPerson,
    PickerSearchEvent,
    PickerSelection,
    PickerTagFilterMode,
} from './people-picker.types';

@UntilDestroy()
@Component({
    selector: 'app-people-picker',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        NgFor,
        ReactiveFormsModule,
        OverlayModule,
        MatCheckboxModule,
        MatRadioModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
    ],
    templateUrl: './people-picker.component.html',
    styleUrls: ['./people-picker.component.scss'],
})
export class PeoplePickerComponent implements OnInit, OnChanges {

    // -- Inputs --
    @Input() label: string = '';
    @Input() placeholder: string = 'Search people…';
    @Input() data: PickerPerson[] = null;
    @Input() preselectedPersonIds: number[] = [];
    @Input() preselectedEmails: string[] = [];
    @Input() disabledIds: number[] = [];
    @Input() maxSelections: number = 0;
    @Input() singleSelect: boolean = false;
    @Input() allowFreeformEmail: boolean = false;
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() helperText: string = '';
    @Input() memberOptions: PickerMemberOptions = { status: 'active', owners: true, allTags: true };
    @Input() unitId: number = null;
    @Input() chipDisplay: 'name' | 'email' = 'name';
    @Input() searchDebounceMs: number = 300;
    @Input() enableTagFilter: boolean = false;
    @Input() tagFilterLabel: string = 'Filter by tag';
    /** Maximum inline tag chips shown per person row before collapsing into "+N". */
    @Input() tagsPerRow: number = 3;

    // -- Outputs --
    @Output() selectionsChange = new EventEmitter<PickerSelection[]>();
    @Output() personAdded = new EventEmitter<PickerPerson>();
    @Output() personRemoved = new EventEmitter<PickerPerson>();
    @Output() emailAdded = new EventEmitter<string>();
    @Output() emailRemoved = new EventEmitter<string>();
    @Output() searchChanged = new EventEmitter<PickerSearchEvent>();

    // -- Template refs --
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
    @ViewChild('root') root: ElementRef<HTMLElement>;
    @ViewChild('overlayOrigin', { read: ElementRef }) overlayOrigin: ElementRef<HTMLElement>;
    @ViewChild(CdkConnectedOverlay) connectedOverlay: CdkConnectedOverlay;

    // CDK overlay anchors below the input by default and flips above when there isn't room.
    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    ];

    // -- State --
    searchControl = new FormControl<string>('');
    selections: PickerSelection[] = [];
    filteredPeople: PickerPerson[] = [];
    isLoading = false;
    isOpen = false;
    isFocused = false;
    showError = false;
    highlightedChipIndex: number | null = null;

    // Tag filter: tri-state — absent = off, 'has' = require, 'not' = exclude.
    availableTags: TagBatchItem[] = [];
    tagFilter = new Map<string, PickerTagFilterMode>();

    private allPeople: PickerPerson[] = [];

    constructor(
        private _membersService: MembersService,
        private _tagsService: TagsService,
        private _cdr: ChangeDetectorRef,
    ) {}

    // ---- Lifecycle ----

    ngOnInit(): void {
        if (!this.data) {
            this.loadMembers();
        }

        if (this.enableTagFilter) {
            this.loadTags();
        }

        this.searchControl.valueChanges.pipe(
            debounceTime(this.searchDebounceMs),
            untilDestroyed(this),
        ).subscribe(term => {
            // SD-7172: always filter locally. Previously, when `data` was provided by the
            // parent, the picker emitted `searchChanged` and skipped filtering — relying on
            // the parent to update `data` in response. Every current caller that passes
            // `[data]` already has the full list in memory and expects client-side filtering;
            // none of them subscribed to `(searchChanged)`. The emit stays as an observation
            // hook for parents that want to react (analytics, async load-more), but no longer
            // gates the filter.
            this.filterPeople(term);
            if (this.data) {
                this.searchChanged.emit({ term, abort$: new Subject() });
            }
        });

        this.applyPreselections();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.allPeople = this.applyDisabledIds(this.data);
            this.filterPeople(this.searchControl.value ?? '');
        }
        if (changes['disabledIds'] && this.allPeople.length > 0) {
            this.allPeople = this.applyDisabledIds(this.allPeople);
            this.filterPeople(this.searchControl.value ?? '');
        }
    }

    // ---- Data Loading ----

    private loadMembers(): void {
        this.isLoading = true;
        const opts = { ...this.memberOptions };
        if (this.unitId) {
            opts.unitId = this.unitId;
        }

        this._membersService.getMembers(null, opts).pipe(
            untilDestroyed(this),
            finalize(() => {
                this.isLoading = false;
                this._cdr.markForCheck();
            }),
        ).subscribe(response => {
            this.allPeople = this.applyDisabledIds(
                (response['memberships'] ?? [])
                    .map(m => new Membership(m))
                    .map(m => mapMembershipToPicker(m)),
            );
            this.filterPeople('');
            this.applyPreselections();
            this._cdr.markForCheck();
        });
    }

    private loadTags(): void {
        this._tagsService.getOrganizationTags('Owner')
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (tags: TagBatchItem[]) => {
                    this.availableTags = (tags ?? [])
                        .filter(t => !!t.tag)
                        .sort((a, b) => a.tag.localeCompare(b.tag));
                    this._cdr.markForCheck();
                },
                error: () => { /* silent — filter UI simply won't show tags */ },
            });
    }

    // ---- Tag filter (tri-state) ----

    /** Cycle: off → has → not → off. */
    cycleTag(tagName: string): void {
        const cur = this.tagFilter.get(tagName);
        if (!cur) {
            this.tagFilter.set(tagName, 'has');
        } else if (cur === 'has') {
            this.tagFilter.set(tagName, 'not');
        } else {
            this.tagFilter.delete(tagName);
        }
        this.filterPeople(this.searchControl.value ?? '');
    }

    getTagState(tagName: string): 'off' | PickerTagFilterMode {
        return this.tagFilter.get(tagName) ?? 'off';
    }

    clearTagFilters(): void {
        if (!this.tagFilter.size) { return; }
        this.tagFilter.clear();
        this.filterPeople(this.searchControl.value ?? '');
    }

    get tagFilterCount(): number {
        return this.tagFilter.size;
    }

    /** Tags in display order — any with an active filter (has/not) float to the top, then alphabetical. */
    get orderedAvailableTags(): TagBatchItem[] {
        if (!this.availableTags.length) { return []; }
        if (!this.tagFilter.size) { return this.availableTags; }
        const filter = this.tagFilter;
        return [...this.availableTags].sort((a, b) => {
            const aa = filter.has(a.tag);
            const bb = filter.has(b.tag);
            if (aa && !bb) { return -1; }
            if (!aa && bb) { return 1; }
            return a.tag.localeCompare(b.tag);
        });
    }

    get filteredNoteText(): string {
        const n = this.tagFilter.size;
        if (!n) { return ''; }
        return `${n} tag filter${n > 1 ? 's' : ''} active`;
    }

    /** Tag names currently in the 'has' (require) state — used to highlight matching row tags. */
    private get requiredTags(): Set<string> {
        const set = new Set<string>();
        for (const [tag, mode] of this.tagFilter) {
            if (mode === 'has') { set.add(tag); }
        }
        return set;
    }

    /** Visible inline tags for a person row (required matches first, then the rest, capped at tagsPerRow). */
    getVisibleTags(person: PickerPerson): string[] {
        if (!person.tags?.length) { return []; }
        const required = this.requiredTags;
        const ordered = [...person.tags].sort((a, b) => {
            const ai = required.has(a) ? 0 : 1;
            const bi = required.has(b) ? 0 : 1;
            return ai - bi;
        });
        return ordered.slice(0, this.tagsPerRow);
    }

    getExtraTagCount(person: PickerPerson): number {
        const total = person.tags?.length ?? 0;
        return Math.max(0, total - this.tagsPerRow);
    }

    isTagRequired(tagName: string): boolean {
        return this.tagFilter.get(tagName) === 'has';
    }

    trackByTagName(index: number, tag: TagBatchItem | string): string {
        return typeof tag === 'string' ? tag : tag.tag;
    }

    // ---- Pre-selection ----

    private applyPreselections(): void {
        if (this.preselectedPersonIds?.length && this.allPeople.length) {
            const idSet = new Set(this.preselectedPersonIds);
            const toAdd = this.allPeople.filter(p => idSet.has(p.id));
            for (const person of toAdd) {
                if (!this.isPersonSelected(person.id)) {
                    this.selections = [...this.selections, { type: 'person', person }];
                }
            }
        }

        if (this.preselectedEmails?.length && this.allowFreeformEmail) {
            for (const email of this.preselectedEmails) {
                if (!this.selections.some(s => s.type === 'email' && s.email === email)) {
                    this.selections = [...this.selections, { type: 'email', email, valid: this.isValidEmail(email) }];
                }
            }
        }

        if (this.selections.length > 0) {
            this.emitSelections();
            this.filterPeople(this.searchControl.value ?? '');
        }
    }

    // ---- Filtering ----

    private applyDisabledIds(people: PickerPerson[]): PickerPerson[] {
        if (!this.disabledIds?.length) { return people; }
        const disabledSet = new Set(this.disabledIds);
        return people.map(p =>
            disabledSet.has(p.id)
                ? { ...p, disabled: true, disabledReason: p.disabledReason || 'This person is not available for selection' }
                : p,
        );
    }

    private passesTagFilter(person: PickerPerson): boolean {
        if (!this.tagFilter.size) { return true; }
        const personTags = new Set(person.tags ?? []);
        for (const [tag, mode] of this.tagFilter) {
            const has = personTags.has(tag);
            if (mode === 'has' && !has) { return false; }
            if (mode === 'not' && has) { return false; }
        }
        return true;
    }

    private passesSearch(person: PickerPerson, q: string): boolean {
        if (!q) { return true; }
        const hay = [person.name, person.email ?? '', ...(person.tags ?? [])].join(' ').toLowerCase();
        return hay.includes(q);
    }

    private filterPeople(term: string): void {
        const q = (typeof term === 'string' ? term : '').toLowerCase().trim();
        this.filteredPeople = this.allPeople.filter(p => this.passesTagFilter(p) && this.passesSearch(p, q));
        this._cdr.markForCheck();
    }

    // ---- Row selection ----

    togglePerson(person: PickerPerson): void {
        if (person.disabled) { return; }
        if (this.isPersonSelected(person.id)) {
            // Single-select mode keeps the click on the active row idempotent —
            // re-clicking a selected person should never deselect it (consumers expect a value).
            if (this.singleSelect) { return; }
            const idx = this.selections.findIndex(s => s.type === 'person' && s.person.id === person.id);
            if (idx >= 0) { this.removeSelection(idx); }
            return;
        }
        if (this.singleSelect && this.selections.length > 0) {
            this.replaceWithPerson(person);
        } else {
            this.addPerson(person);
        }
        // Single-select commits auto-close the dropdown so the new selection visibly
        // settles into the input chip. Without this, a second pick reads as a
        // deselection-then-reselection rather than a replacement.
        if (this.singleSelect) { this.closeDropdown(); }
        // Clear the residual search query once a person is committed so the input
        // reflects the selection chip rather than the text that was typed to find it
        // (mirrors commitFreeformEmail). Deselection paths above early-return first.
        this.resetInput();
    }

    private replaceWithPerson(person: PickerPerson): void {
        const previous = this.selections;
        this.selections = [{ type: 'person', person }];
        for (const sel of previous) {
            if (sel.type === 'person') { this.personRemoved.emit(sel.person); }
            if (sel.type === 'email') { this.emailRemoved.emit(sel.email); }
        }
        this.personAdded.emit(person);
        this.emitSelections();
        this.syncDisabledState();
        this._cdr.markForCheck();
    }

    addPerson(person: PickerPerson): void {
        if (this.atMax) { return; }
        if (this.isPersonSelected(person.id)) { return; }

        this.selections = [...this.selections, { type: 'person', person }];
        this.personAdded.emit(person);
        this.emitSelections();
        this.syncDisabledState();
        this._cdr.markForCheck();
    }

    commitFreeformEmail(rawEmail: string): void {
        if (this.atMax) { return; }
        const email = rawEmail.trim();
        if (!email) { return; }
        if (this.selections.some(s => s.type === 'email' && s.email === email)) {
            this.resetInput();
            return;
        }
        const valid = this.isValidEmail(email);
        this.selections = [...this.selections, { type: 'email', email, valid }];
        this.emailAdded.emit(email);
        this.emitSelections();
        this.resetInput();
        this.syncDisabledState();
        this._cdr.markForCheck();
    }

    removeSelection(index: number): void {
        const removed = this.selections[index];
        this.selections = this.selections.filter((_, i) => i !== index);
        this.highlightedChipIndex = null;

        if (removed.type === 'person') { this.personRemoved.emit(removed.person); }
        if (removed.type === 'email') { this.emailRemoved.emit(removed.email); }

        this.emitSelections();
        this.syncDisabledState();
        this._cdr.markForCheck();
    }

    // ---- Bulk actions ----

    clearPicks(): void {
        if (!this.selections.length) { return; }
        const previouslySelectedPeople = this.selections
            .filter((s): s is { type: 'person'; person: PickerPerson } => s.type === 'person')
            .map(s => s.person);
        const previouslySelectedEmails = this.selections
            .filter((s): s is { type: 'email'; email: string; valid: boolean } => s.type === 'email')
            .map(s => s.email);

        this.selections = [];
        for (const p of previouslySelectedPeople) { this.personRemoved.emit(p); }
        for (const e of previouslySelectedEmails) { this.emailRemoved.emit(e); }

        this.emitSelections();
        this.syncDisabledState();
        this._cdr.markForCheck();
    }

    toggleAllVisible(): void {
        const visible = this.filteredPeople.filter(p => !p.disabled);
        if (!visible.length) { return; }

        const allSelected = visible.every(p => this.isPersonSelected(p.id));
        if (allSelected) {
            // Deselect all visible.
            const visibleIds = new Set(visible.map(p => p.id));
            const toRemove = this.selections.filter(
                (s): s is { type: 'person'; person: PickerPerson } =>
                    s.type === 'person' && visibleIds.has(s.person.id),
            );
            this.selections = this.selections.filter(
                s => s.type !== 'person' || !visibleIds.has(s.person.id),
            );
            for (const s of toRemove) { this.personRemoved.emit(s.person); }
        } else {
            // Select all visible (respect maxSelections).
            const unselected = visible.filter(p => !this.isPersonSelected(p.id));
            const slots = this.maxSelections > 0
                ? Math.max(0, this.maxSelections - this.selections.length)
                : unselected.length;
            const additions = unselected.slice(0, slots);
            if (additions.length) {
                this.selections = [
                    ...this.selections,
                    ...additions.map(person => ({ type: 'person' as const, person })),
                ];
                for (const p of additions) { this.personAdded.emit(p); }
            }
        }
        this.emitSelections();
        this.syncDisabledState();
        this._cdr.markForCheck();
    }

    get isAllVisibleSelected(): boolean {
        const visible = this.filteredPeople.filter(p => !p.disabled);
        return visible.length > 0 && visible.every(p => this.isPersonSelected(p.id));
    }

    get bulkText(): string {
        const total = this.selections.length;
        const isFiltered = this.tagFilter.size > 0 || !!this.searchControl.value?.trim();
        if (isFiltered) {
            return `${this.filteredPeople.length} shown · ${total} picked`;
        }
        return `${total} picked`;
    }

    get selectAllButtonText(): string {
        return this.isAllVisibleSelected ? 'Deselect all visible' : 'Select all visible';
    }

    // ---- Focus / Open / Close ----

    focusInput(): void {
        if (!this.disabled && !this.atMax) {
            this.searchInput?.nativeElement?.focus();
        }
    }

    onFocus(): void {
        this._captureOverlayWidth();
        this.isFocused = true;
        this.isOpen = true;
        this._cdr.markForCheck();
    }

    onBlur(): void {
        const val = this.searchControl.value?.trim() ?? '';
        if (this.allowFreeformEmail && val) {
            if (this.looksLikeEmail(val)) {
                this.commitFreeformEmail(val);
            } else {
                this.searchControl.setValue('', { emitEvent: true });
                if (this.searchInput?.nativeElement) {
                    this.searchInput.nativeElement.value = '';
                }
                this.filterPeople('');
            }
        }
        // Defer closing so clicks inside the dropdown can run first.
        setTimeout(() => {
            this.isFocused = false;
            this._cdr.markForCheck();
        }, 200);
    }

    /** Prevent mousedowns inside the dropdown from blurring the input. */
    onDropdownMousedown(event: MouseEvent): void {
        event.preventDefault();
    }

    closeDropdown(): void {
        this.isOpen = false;
        this.isFocused = false;
        this._cdr.markForCheck();
    }

    onOverlayOutsideClick(event: MouseEvent): void {
        if (!this.isOpen) { return; }
        const target = event.target as Node;
        if (!target || !document.contains(target)) { return; }
        // Trigger clicks already toggle focus/blur — let the input handle them so we don't
        // immediately reopen after closing.
        if (this.root?.nativeElement?.contains(target)) { return; }
        this.closeDropdown();
    }

    /**
     * Overlay width snapshot — captured when the dropdown opens (in onFocus) so the CDK
     * connected-overlay sizes to match the trigger. Reading the width via a getter on
     * every change-detection cycle was producing stale/zero values inside dialogs (the
     * overlay rendered at content-natural width instead of trigger width).
     */
    overlayWidth = 0;

    private _captureOverlayWidth(): void {
        this.overlayWidth = this.overlayOrigin?.nativeElement?.getBoundingClientRect().width ?? 0;
    }

    /**
     * Re-apply the overlay width via the live overlayRef whenever the overlay attaches.
     * The `[cdkConnectedOverlayWidth]` input binds at directive instantiation; setting
     * width via `updateSize()` after attach is the reliable path to size the overlay
     * to the trigger.
     */
    onOverlayAttach(): void {
        this._captureOverlayWidth();
        this.connectedOverlay?.overlayRef?.updateSize({ width: this.overlayWidth });
    }

    // ---- Keyboard ----

    onKeydown(event: KeyboardEvent): void {
        const val = this.searchControl.value?.trim() ?? '';

        if (event.key === 'Backspace' && !val && this.selections.length > 0) {
            const lastIndex = this.selections.length - 1;
            if (this.highlightedChipIndex === lastIndex) {
                this.removeSelection(lastIndex);
                this.highlightedChipIndex = null;
            } else {
                this.highlightedChipIndex = lastIndex;
                this._cdr.markForCheck();
            }
            return;
        }

        if (event.key !== 'Backspace' && this.highlightedChipIndex !== null) {
            this.highlightedChipIndex = null;
            this._cdr.markForCheck();
        }

        if ((event.key === ',' || event.key === 'Tab') && val && this.allowFreeformEmail) {
            if (event.key === ',') { event.preventDefault(); }
            const cleaned = val.replace(/,$/, '');
            if (cleaned && this.looksLikeEmail(cleaned)) {
                this.commitFreeformEmail(cleaned);
            }
        }

        if (event.key === 'Escape') {
            this.searchInput.nativeElement.blur();
            this.closeDropdown();
        }
    }

    // ---- Helpers ----

    get atMax(): boolean {
        return this.maxSelections > 0 && this.selections.length >= this.maxSelections;
    }

    get hasEmailAction(): boolean {
        return this.allowFreeformEmail &&
            !!this.searchControl.value?.trim() &&
            this.looksLikeEmail(this.searchControl.value.trim()) &&
            !this.selections.some(s => s.type === 'email' && s.email === this.searchControl.value.trim());
    }

    get currentEmailInput(): string {
        return this.searchControl.value?.trim() ?? '';
    }

    get currentPlaceholder(): string {
        if (this.atMax) { return ''; }
        // Single-select reuses the configured placeholder so users can type to swap selections —
        // "Add more…" would mislead consumers who only allow one value.
        if (this.singleSelect && this.selections.length > 0) {
            return this.placeholder || 'Search people…';
        }
        if (this.selections.length > 0) { return 'Add more…'; }
        return this.allowFreeformEmail ? 'Search people or type an email…' : (this.placeholder || 'Search people…');
    }

    isPersonSelected(id: number): boolean {
        return this.selections.some(s => s.type === 'person' && s.person.id === id);
    }

    isValidEmail(str: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
    }

    looksLikeEmail(str: string): boolean {
        return /^[^\s@]+@[^\s@]*$/.test(str) && str.includes('@');
    }

    trackBySelection(index: number, sel: PickerSelection): string | number {
        return sel.type === 'person' ? sel.person.id : sel.email;
    }

    trackByPerson(index: number, person: PickerPerson): number {
        return person.id;
    }

    // ---- Internal ----

    private emitSelections(): void {
        this.selectionsChange.emit([...this.selections]);
    }

    private resetInput(): void {
        this.searchControl.setValue('', { emitEvent: false });
        if (this.searchInput?.nativeElement) {
            this.searchInput.nativeElement.value = '';
        }
        this.filterPeople('');
    }

    private syncDisabledState(): void {
        const shouldDisable = this.disabled || this.atMax;
        if (shouldDisable && !this.searchControl.disabled) {
            this.searchControl.disable({ emitEvent: false });
        } else if (!shouldDisable && this.searchControl.disabled) {
            this.searchControl.enable({ emitEvent: false });
        }
    }

}
